import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Building2, Hospital, Layers, Users, CreditCard, ScrollText,
  Settings, Stethoscope, Boxes, ShieldCheck, ShieldAlert,
  ClipboardList, UserPlus, Receipt, CalendarClock, CalendarDays, BedDouble, Bed, Wallet, Link2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFacility } from "@/context/FacilityContext";

const PLATFORM_NAV = [
  { to: "/platform", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-platform-dashboard" },
  { to: "/platform/organisations", label: "Organisations", icon: Building2, testId: "nav-organisations" },
  { to: "/platform/facilities", label: "Facilities", icon: Hospital, testId: "nav-facilities" },
  { to: "/platform/modules", label: "Modules", icon: Layers, testId: "nav-modules" },
  { to: "/platform/users", label: "Users", icon: Users, testId: "nav-users" },
  { to: "/platform/subscriptions", label: "Subscriptions", icon: CreditCard, testId: "nav-subscriptions" },
  { to: "/platform/audit", label: "Audit Logs", icon: ScrollText, testId: "nav-audit" },
  { to: "/platform/settings", label: "Platform Settings", icon: Settings, testId: "nav-platform-settings" },
];

const TENANT_NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, perm: "dashboard:view", testId: "nav-dashboard" },
  { to: "/app/facility", label: "Facility", icon: Hospital, perm: "facility:view", testId: "nav-facility" },
  { to: "/app/departments", label: "Departments", icon: Boxes, perm: "department:view", testId: "nav-departments" },
  { to: "/app/services", label: "Services", icon: Stethoscope, perm: "service:view", testId: "nav-services" },
  { to: "/app/charges", label: "Charge Management", icon: CreditCard, perm: "bed:view", testId: "nav-charges" },
  { to: "/app/users", label: "Users", icon: Users, perm: "user:view", testId: "nav-tenant-users" },
  { to: "/app/roles", label: "Roles & Permissions", icon: ShieldCheck, perm: "role:view", testId: "nav-roles" },
  { to: "/app/modules", label: "Enabled Modules", icon: Layers, perm: "module:view", testId: "nav-tenant-modules" },
  { to: "/app/audit", label: "Audit Logs", icon: ScrollText, perm: "audit:view", testId: "nav-tenant-audit" },
  { to: "/app/settings", label: "Settings", icon: Settings, perm: "settings:view", testId: "nav-settings" },
];

const IPD_NAV = [
  { to: "/app/ipd/admissions", label: "Admissions", icon: BedDouble, end: true, perm: "ipd:view", testId: "nav-ipd-admissions" },
  { to: "/app/ipd/admit", label: "Admit Patient", icon: UserPlus, perm: "ipd:create", testId: "nav-ipd-admit" },
  { to: "/app/ipd/beds", label: "Bed Management", icon: Bed, perm: "bed:view", testId: "nav-ipd-beds" },
];

const FINANCE_NAV = [
  { to: "/app/finance", label: "Finance", icon: Wallet, perm: "finance:view", testId: "nav-finance" },
];

const OPD_NAV = [
  { to: "/app/opd", label: "OPD Queue", icon: ClipboardList, end: true, perm: "opd:view", testId: "nav-opd-queue" },
  { to: "/app/opd/patients?new=1", label: "New Patient", icon: UserPlus, perm: "patient:create", testId: "nav-new-patient" },
  { to: "/app/opd/appointments", label: "Appointments", icon: CalendarDays, perm: "opd:view", testId: "nav-opd-appointments" },
  { to: "/app/opd/patients", label: "Patients", icon: Users, perm: "patient:view", testId: "nav-opd-patients" },
  { to: "/app/opd/billing", label: "Billing", icon: Receipt, perm: "opd_billing:view", testId: "nav-opd-billing" },
  { to: "/app/opd/followups", label: "Follow-ups", icon: CalendarClock, perm: "opd:view", testId: "nav-opd-followups" },
  { to: "/app/opd/connections", label: "Connection Requests", icon: Link2, perm: "opd:view", testId: "nav-opd-connections" },
];

export function Sidebar({ onNavigate }) {
  const { auth } = useAuth();
  const facilityCtx = useFacility();
  const isPlatform = auth?.is_platform_admin;
  const mods = facilityCtx?.activeModules || [];

  const sections = isPlatform
    ? [{ title: null, items: PLATFORM_NAV }]
    : [
        { title: null, items: [TENANT_NAV[0]] },
        { title: "OPD", show: mods.includes("opd"), items: OPD_NAV },
        { title: "IPD", show: mods.includes("ipd"), items: IPD_NAV },
        { title: "Accounts", items: FINANCE_NAV },
        { title: "Administration", items: TENANT_NAV.slice(1) },
      ];

  return (
    <aside className="w-[260px] shrink-0 bg-slate-900 text-slate-300 flex flex-col h-full sidebar-scroll" data-testid="app-sidebar">
      <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-800">
        <div className="h-8 w-8 rounded-md bg-blue-600 grid place-items-center text-white font-display font-bold">M</div>
        <div className="leading-tight">
          <div className="font-display font-semibold text-white text-[15px]">Mediva Solutions</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
            {isPlatform ? "Platform Console" : "Operations"}
          </div>
        </div>
      </div>

      {isPlatform && (
        <div className="mx-3 mt-3 mb-1 px-3 py-2 rounded-md bg-blue-950/50 border border-blue-900/60 flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-blue-400" />
          <span className="text-[11px] font-medium text-blue-200">Super Admin — above all tenants</span>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto sidebar-scroll px-3 py-3 space-y-4">
        {sections.map((sec, si) => {
          if (sec.show === false) return null;
          const items = sec.items.filter((i) => isPlatform || !i.perm || facilityCtx?.has(i.perm));
          if (items.length === 0) return null;
          return (
            <div key={si} className="space-y-0.5">
              {sec.title && <div className="px-3 pb-1 text-[10px] font-mono uppercase tracking-widest text-slate-600">{sec.title}</div>}
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                      isActive
                        ? "bg-slate-800 text-white border-l-2 border-blue-500 font-medium"
                        : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 border-l-2 border-transparent"
                    }`
                  }
                >
                  <item.icon className="h-[18px] w-[18px]" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      <div className="px-5 py-3 border-t border-slate-800 text-[10px] font-mono text-slate-600">
        Mediva Solutions
      </div>
    </aside>
  );
}
